import { memo, useEffect, useMemo, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Alert, AlertIcon } from 'components/v4';
import { Box } from '@chakra-ui/react';
import { useServicesDashboardDispatch } from 'context/ServicesDashboardContext';
import {
    APP_AUTH,
    APP_AUTH_GCCH,
    EIGHTBYEIGHT,
    EIGHTBYEIGHT_AUTH,
    MICROSOFT_GRAPH,
    RINGCENTRAL,
    RINGCENTRAL_AUTH,
    DIALPAD,
    DIALPAD_AUTH,
    WEBEX_AUTH,
    ZOOM_AUTH,
} from './constants';
import { PlatformEnums } from '@/utils/platforms';

// NEW
import DashboardAccordion from 'pages/dashboards/v5/components/DashboardAccordion';
import DashboardServicesSkeleton from './DashboardServicesSkeleton';
import DashboardServicesEmptySkeleton from './DashboardServicesEmptySkeleton';
import CompanyApproveBanner from 'pages/dashboards/v5/components/CompanyApproveBanner';

const DashboardServices = ({
    serviceWizard,
    services,
    cachedAppAuths = [],
    dashboardData,
    companyData,
    isDashboardLoading,
    msTeamsAppAuthDetail,
}) => {
    const { currentCompany: companyID } = useSelector(
        (state) => state.navigationLists,
    );
    const partnerName = useSelector(
        (state) => state.navigationLists?.currentCompanyData?.partnerName,
    );
    const { state } = useLocation();
    const dispatch = useServicesDashboardDispatch();
    const appAuths = useMemo(() => {
        return dashboardData ? dashboardData?.appAuths || [] : cachedAppAuths;
    }, [cachedAppAuths, dashboardData]);
    const msCommercialAuth = useMemo(() => {
        return (
            appAuths.find(
                (a) =>
                    a?.appAuthTypeID === MICROSOFT_GRAPH &&
                    a?.platformID === PlatformEnums.TEAMS,
            ) ||
            // NOTE: fix bug #2335 where we see a shadow Microsoft Graph AppAuth
            // even we only have GCC High AppAuth
            // as they share appAuthTypeID but different platformID.
            appAuths.find(
                (a) =>
                    a?.appAuthTypeID === MICROSOFT_GRAPH &&
                    a?.platformID !== PlatformEnums.GCC,
            ) ||
            null
        );
    }, [appAuths]);
    // 2629: the /Services payload's appAuths entry for Microsoft Teams
    // doesn't carry msGraphAppInstanceID/lastAccessSuccess, so merge in the
    // enriched /appauth/{id} detail fetched by the parent before this flows
    // into AppAuthCard's Migrate to New Sync logic.
    const enrichedMsCommercialAuth = useMemo(() => {
        return msCommercialAuth
            ? { ...msCommercialAuth, ...msTeamsAppAuthDetail }
            : msCommercialAuth;
    }, [msCommercialAuth, msTeamsAppAuthDetail]);
    const zoomAppAuthService = useMemo(
        () => appAuths.find((a) => a?.appAuthTypeID === ZOOM_AUTH) || null,
        [appAuths],
    );
    const eightByEightAppAuthService = useMemo(
        () =>
            appAuths.find(
                (a) =>
                    a?.appAuthTypeID === EIGHTBYEIGHT_AUTH ||
                    a?.appAuthTypeID === EIGHTBYEIGHT,
            ) || null,
        [appAuths],
    );
    const webexAppAuthService = useMemo(
        () => appAuths.find((a) => a?.appAuthTypeID === WEBEX_AUTH) || null,
        [appAuths],
    );
    const gccHighAppAuthService = useMemo(
        () =>
            appAuths.find(
                (a) =>
                    a?.appAuthTypeID === MICROSOFT_GRAPH &&
                    a?.platformID === PlatformEnums.GCC,
            ) || null,
        [appAuths],
    );
    const ringCentralAppAuthService = useMemo(
        () =>
            appAuths.find(
                (a) =>
                    a?.appAuthTypeID === RINGCENTRAL ||
                    a?.appAuthTypeID === RINGCENTRAL_AUTH,
            ) || null,
        [appAuths],
    );
    const dialPadAppAuthService = useMemo(
        () =>
            appAuths.find(
                (a) =>
                    a?.appAuthTypeID === DIALPAD ||
                    a?.appAuthTypeID === DIALPAD_AUTH,
            ) || null,
        [appAuths],
    );
    const lastPayloadKeyRef = useRef('');
    const traceSnapshotRef = useRef('');

    const { setIsCreatingService } = serviceWizard;

    const AppAuthServiceType = msCommercialAuth?.appAuthTypeID;
    const ZoomAppAuthServiceType = zoomAppAuthService?.appAuthTypeID;
    const EightByEightAppAuthServiceType =
        eightByEightAppAuthService?.appAuthTypeID;
    const WebexAppAuthServiceType = webexAppAuthService?.appAuthTypeID;
    const GccHighAppAuthServiceType = gccHighAppAuthService?.appAuthTypeID;
    const RingCentralAppAuthServiceType =
        ringCentralAppAuthService?.appAuthTypeID;
    const DialPadAppAuthServiceType = dialPadAppAuthService?.appAuthTypeID;

    useEffect(() => {
        if (!import.meta.env.DEV) return;
        if (!window.location.search.includes('perf=1')) return;

        const snapshot = {
            companyID,
            renderedServices: services?.length ?? null,
            dashboardServices: dashboardData?.services?.length || 0,
            dashboardAppAuths: appAuths.length,
            msCommercialAuthId: msCommercialAuth?.id || null,
            isDashboardLoading,
        };
        const nextSnapshot = JSON.stringify(snapshot);

        if (traceSnapshotRef.current === nextSnapshot) return;
        traceSnapshotRef.current = nextSnapshot;

        console.info(`[DASH:trace] DashboardServices ${nextSnapshot}`);
    }, [
        appAuths.length,
        companyID,
        dashboardData,
        isDashboardLoading,
        msCommercialAuth,
        services,
    ]);

    useEffect(() => {
        if (!companyID || !companyData || !dashboardData) return;

        const servicesKey = (dashboardData.services || [])
            .map(
                (s) =>
                    `${s?.id}:${s?.serviceType}:${s?.status}:${s?.statusDisplay?.statusDisplayLevel}`,
            )
            .join('|');
        const appAuthsKey = (appAuths || [])
            .map(
                (a) =>
                    `${a?.id}:${a?.appAuthTypeID}:${a?.platformID}:${a?.status}:${a?.statusDisplay?.statusDisplayLevel}`,
            )
            .join('|');
        const nextPayloadKey = `${companyID}::${servicesKey}::${appAuthsKey}`;
        if (lastPayloadKeyRef.current === nextPayloadKey) {
            if (
                import.meta.env.DEV &&
                window.location.search.includes('perf=1')
            ) {
                console.info(
                    `[DASH:hydrate] DashboardServices skip same-payload company=${companyID} services=${dashboardData.services?.length || 0} appAuths=${appAuths.length}`,
                );
            }
            return;
        }
        lastPayloadKeyRef.current = nextPayloadKey;

        if (import.meta.env.DEV && window.location.search.includes('perf=1')) {
            console.info(
                `[DASH:hydrate] DashboardServices apply company=${companyID} services=${dashboardData.services?.length || 0} appAuths=${appAuths.length}`,
            );
        }

        unstable_batchedUpdates(() => {
            if (msCommercialAuth) {
                dispatch({
                    type: `${APP_AUTH}/loaded_status`,
                    payload: {
                        serviceType: APP_AUTH,
                        service: {
                            ...msCommercialAuth,
                            company: companyData,
                        },
                    },
                });
            }

            if (gccHighAppAuthService) {
                dispatch({
                    type: `${APP_AUTH_GCCH}/loaded_status`,
                    payload: {
                        serviceType: APP_AUTH_GCCH,
                        service: {
                            ...gccHighAppAuthService,
                            company: companyData,
                        },
                    },
                });
            }

            appAuths.forEach((appAuth) => {
                if (!appAuth?.appAuthTypeID) return;
                dispatch({
                    type: `${appAuth.appAuthTypeID}/loaded_status`,
                    payload: {
                        serviceType: appAuth.appAuthTypeID,
                        service: {
                            ...appAuth,
                            company: companyData,
                        },
                    },
                });
            });

            // Preserve legacy status keys for certain auth types
            const eightByEightAuth = appAuths.find(
                (a) =>
                    a?.appAuthTypeID === EIGHTBYEIGHT ||
                    a?.appAuthTypeID === EIGHTBYEIGHT_AUTH,
            );
            if (eightByEightAuth) {
                dispatch({
                    type: `${EIGHTBYEIGHT_AUTH}/loaded_status`,
                    payload: {
                        serviceType: EIGHTBYEIGHT_AUTH,
                        service: {
                            ...eightByEightAuth,
                            company: companyData,
                        },
                    },
                });
            }

            const zoomAuth = appAuths.find(
                (a) => a?.appAuthTypeID === ZOOM_AUTH,
            );
            if (zoomAuth) {
                dispatch({
                    type: `${ZOOM_AUTH}/loaded_status`,
                    payload: {
                        serviceType: ZOOM_AUTH,
                        service: {
                            ...zoomAuth,
                            company: companyData,
                        },
                    },
                });
            }

            const webexAuth = appAuths.find(
                (a) => a?.appAuthTypeID === WEBEX_AUTH,
            );
            if (webexAuth) {
                dispatch({
                    type: `${WEBEX_AUTH}/loaded_status`,
                    payload: {
                        serviceType: WEBEX_AUTH,
                        service: {
                            ...webexAuth,
                            company: companyData,
                        },
                    },
                });
            }

            const ringCentralAuth = appAuths.find(
                (a) =>
                    a?.appAuthTypeID === RINGCENTRAL ||
                    a?.appAuthTypeID === RINGCENTRAL_AUTH,
            );
            if (ringCentralAuth) {
                dispatch({
                    type: `${RINGCENTRAL_AUTH}/loaded_status`,
                    payload: {
                        serviceType: RINGCENTRAL_AUTH,
                        service: {
                            ...ringCentralAuth,
                            company: companyData,
                        },
                    },
                });
            }

            const dialPadAuth = appAuths.find(
                (a) =>
                    a?.appAuthTypeID === DIALPAD ||
                    a?.appAuthTypeID === DIALPAD_AUTH,
            );
            if (dialPadAuth) {
                dispatch({
                    type: `${DIALPAD_AUTH}/loaded_status`,
                    payload: {
                        serviceType: DIALPAD_AUTH,
                        service: {
                            ...dialPadAuth,
                            company: companyData,
                        },
                    },
                });
            }

            (dashboardData.services || []).forEach((service) => {
                if (!service?.serviceType) return;
                dispatch({
                    type: `${service.serviceType}/loaded_status`,
                    payload: {
                        serviceType: service.serviceType,
                        service,
                    },
                });
            });
        });
    }, [
        appAuths,
        companyData,
        companyID,
        dashboardData,
        dispatch,
        gccHighAppAuthService,
        msCommercialAuth,
    ]);

    useEffect(() => {
        // Reset create service loading flag here
        // That way, when creating a service, and authing, the loading spinner
        // will stay true until they leave the page
        setIsCreatingService(false);
    }, [companyID, setIsCreatingService]);

    useEffect(() => {
        if (!state?.message) return;

        let isMounted = true;
        if (state?.message) {
            toast.error(state?.message, { autoClose: 3000 });
        }

        if (isMounted) {
            window.history.replaceState({}, ''); // HACK: reset history state when refresh
        }
        return () => {
            isMounted = false;
            toast.dismiss();
        };
    }, [state?.message]);

    const hasAnyCards = Boolean((services?.length || 0) + appAuths.length);

    if (services === null && !appAuths.length) {
        return <DashboardServicesSkeleton />;
    }

    // While hydrating the full /Services payload we keep rendering per-service
    // skeleton cards seeded from the cached loading shape when available.
    if (isDashboardLoading && !hasAnyCards) {
        return <DashboardServicesEmptySkeleton />;
    }

    if (!hasAnyCards) {
        return (
            <Box padding="20px 0px" textAlign="center">
                <Alert mb="0.5rem" status="info">
                    <AlertIcon />
                    You haven&apos;t created any services. Click the &nbsp;
                    <b>Add New Service</b>&nbsp; button to create your first
                    service.
                </Alert>
            </Box>
        );
    }

    return (
        <>
            <CompanyApproveBanner
                companyData={companyData}
                partnerName={partnerName}
                appAuth={msCommercialAuth}
            />
            <DashboardAccordion
                services={services || []}
                serviceWizard={serviceWizard}
                companyData={companyData}
                companyAppAuth={appAuths}
                msServiceAuth={enrichedMsCommercialAuth || {}}
                AppAuthService={msCommercialAuth}
                ZoomAppAuthService={zoomAppAuthService}
                GccHighAppAuthService={gccHighAppAuthService}
                EightByEightAppAuthService={eightByEightAppAuthService}
                RingCentralAppAuthService={ringCentralAppAuthService}
                DialPadAppAuthService={dialPadAppAuthService}
                WebexAppAuthService={webexAppAuthService}
                AppAuthServiceType={AppAuthServiceType}
                ZoomAppAuthServiceType={ZoomAppAuthServiceType}
                EightByEightAppAuthServiceType={EightByEightAppAuthServiceType}
                RingCentralAppAuthServiceType={RingCentralAppAuthServiceType}
                DialPadAppAuthServiceType={DialPadAppAuthServiceType}
                WebexAppAuthServiceType={WebexAppAuthServiceType}
                GccHighAppAuthServiceType={GccHighAppAuthServiceType}
            />
        </>
    );
};

DashboardServices.displayName = 'DashboardServices';

export default memo(DashboardServices);
